import React, { useRef, useState, useEffect, useCallback } from "react";
import { Input } from "reactstrap";

const API_KEY = "AIzaSyD2BbvjWvF73TNFX3c0BTBlN6kmNc0Rheg";
const SCRIPT_SRC = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&v=weekly&loading=async`;
const SCRIPT_ID = "qiktruck-chq-gmaps-places";

let _ready = false;
let _promise = null;

function loadMaps() {
  if (_ready) return Promise.resolve();
  if (_promise) return _promise;

  _promise = new Promise((resolve) => {
    if (window.google?.maps?.places?.AutocompleteSuggestion) {
      _ready = true;
      resolve();
      return;
    }
    if (
      document.getElementById(SCRIPT_ID) ||
      document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
    ) {
      const check = setInterval(() => {
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
          clearInterval(check);
          _ready = true;
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(check);
        resolve();
      }, 10000);
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      _ready = true;
      resolve();
    };
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return _promise;
}

const readLat = (location) => {
  if (!location) return null;
  if (typeof location.lat === "function") return location.lat();
  if (location.lat != null) return location.lat;
  return null;
};

const readLng = (location) => {
  if (!location) return null;
  if (typeof location.lng === "function") return location.lng();
  if (location.lng != null) return location.lng;
  return null;
};

/** Legacy PlaceResult-shaped object for existing callers (BookTripModal, etc.). */
const buildNormalizedPlace = (place) => {
  const location = place?.location || null;
  const lat = readLat(location);
  const lng = readLng(location);
  const formattedAddress = place?.formattedAddress || "";

  return {
    place_id: place?.id || "",
    formatted_address: formattedAddress,
    latitude: lat,
    longitude: lng,
    geometry:
      lat != null && lng != null
        ? {
            location: {
              lat: () => lat,
              lng: () => lng,
            },
          }
        : null,
    raw: place,
  };
};

const PlacesAutocomplete = ({
  type,
  className,
  id,
  placeholder,
  required,
  onPlaceSelected,
  onSelect,
  onInputChange,
  style,
  defaultValue,
  disabled,
  "aria-label": ariaLabel,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(_ready);
  const wrapperRef = useRef(null);
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);
  const nameRef = useRef(`pa-${id || Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!ready) loadMaps().then(() => setReady(true));
  }, [ready]);

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const notifySelection = useCallback(
    (normalized) => {
      if (onPlaceSelected) onPlaceSelected(normalized);
      if (onSelect) {
        onSelect({
          lat: normalized.latitude,
          lng: normalized.longitude,
          address: normalized.formatted_address,
          description: normalized.formatted_address,
        });
      }
    },
    [onPlaceSelected, onSelect]
  );

  const fetchPredictions = useCallback(
    async (input) => {
      if (!ready || !window.google?.maps?.places?.AutocompleteSuggestion) return;
      if (!sessionRef.current) {
        sessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      try {
        const { suggestions } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionRef.current,
            includedRegionCodes: ["za"],
          });
        setPredictions((suggestions || []).filter((s) => s.placePrediction));
      } catch {
        setPredictions([]);
      }
    },
    [ready]
  );

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setInputValue(val);
      if (onInputChange) onInputChange(val);
      clearTimeout(debounceRef.current);
      if (val.length > 1) {
        setOpen(true);
        debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
      } else {
        setOpen(false);
        setPredictions([]);
      }
    },
    [fetchPredictions, onInputChange]
  );

  const handleSelect = useCallback(
    async (suggestion) => {
      const pred = suggestion.placePrediction;
      const label = pred.text.text;
      setInputValue(label);
      setOpen(false);
      setPredictions([]);

      try {
        const place = pred.toPlace();
        await place.fetchFields({
          fields: ["id", "displayName", "formattedAddress", "location", "addressComponents"],
        });
        sessionRef.current = null;
        notifySelection(buildNormalizedPlace(place));
      } catch {
        notifySelection({
          place_id: pred.placeId || "",
          formatted_address: label,
          latitude: null,
          longitude: null,
          geometry: null,
          raw: null,
        });
      }
    },
    [notifySelection]
  );

  if (style?.display === "none") return null;

  return (
    <div ref={wrapperRef} className="placesAutocomplete" style={{ position: "relative" }}>
      <Input
        style={style}
        type={type}
        className={className}
        id={id}
        placeholder={placeholder}
        required={required}
        aria-label={ariaLabel}
        autoComplete="new-password"
        name={nameRef.current}
        value={inputValue}
        disabled={disabled}
        onChange={handleChange}
        onFocus={() => {
          if (inputValue.length > 1 && predictions.length > 0) setOpen(true);
        }}
      />
      {open && predictions.length > 0 && (
        <ul className="places-ac-dropdown">
          {predictions.map((s, i) => {
            const pred = s.placePrediction;
            return (
              <li
                key={pred.placeId || i}
                onMouseDown={() => handleSelect(s)}
                className="places-ac-item"
              >
                <i className="mdi mdi-map-marker-outline places-ac-icon" />
                <span>{pred.text.text}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
